import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { StrictMode } from 'react';
import { AuthProvider } from 'react-oidc-context';
import { createRoot } from 'react-dom/client';

import type { SigninState } from './sessionContext';
import { App } from './App';
import { oidcSettings } from './auth';
import { loadConfig } from './config';
import { navigate } from './router';
import { theme } from './theme';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('missing #root element');
const root = createRoot(rootEl);

function render(node: React.ReactNode) {
  root.render(
    <StrictMode>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {node}
      </ThemeProvider>
    </StrictMode>,
  );
}

loadConfig()
  .then((config) => {
    if (!config.oidc) {
      render(<App config={config} />);
      return;
    }
    render(
      <AuthProvider
        {...oidcSettings(config.oidc)}
        onSigninCallback={(user) => {
          // Drop ?code=&state= from the address bar after the redirect back,
          // and return to the page the sign-in started from.
          const state = user?.state as SigninState | undefined;
          const returnTo = state?.returnTo?.startsWith('/')
            ? state.returnTo
            : window.location.pathname;
          window.history.replaceState({}, document.title, window.location.pathname);
          navigate(returnTo, true);
        }}
      >
        <App config={config} />
      </AuthProvider>,
    );
  })
  .catch((err: unknown) => {
    render(
      <p role="alert" style={{ padding: 24 }}>
        IllumiLink could not start: {err instanceof Error ? err.message : String(err)}
      </p>,
    );
  });
